import { useGameStore } from '../../core/store';
import { onFullscreenButtonPointerDown } from '../../core/fullscreen';
import {
  setCrowdIntensity,
  setIntroMuted,
  stopIntroBed,
  startTunnelBed,
  unlockAudio,
  preloadAudio,
  play,
} from '../audio';
import { useFullscreenFlag } from './FullscreenCanvasSync';
import './site-chrome-mobile.css';

function SpeakerIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden className="site-m__icon">
        <path
          fill="currentColor"
          d="M4 9v6h3.5L12 19V5L7.5 9H4zm11.5 3l2.1-2.1 1.4 1.4L17 13.4l2 2-1.4 1.4-2.1-2.1-2.1 2.1-1.4-1.4 2.1-2 2.1-2.1 1.4 1.4z"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="site-m__icon">
      <path
        fill="currentColor"
        d="M4 9v6h3.5L12 19V5L7.5 9H4zm9.5 3c0-1.8-1-3.3-2.5-4v8c1.5-.7 2.5-2.2 2.5-4zm2.5 0c0 2.9-1.6 5.4-4 6.7v2.1c3.4-1.4 5.8-4.8 5.8-8.8S17.4 5.6 14 4.2v2.1c2.4 1.3 4 3.8 4 6.7z"
      />
    </svg>
  );
}

function canFullscreen() {
  return (
    typeof document !== 'undefined' &&
    !!(
      document.documentElement.requestFullscreen ||
      (
        document.documentElement as HTMLElement & {
          webkitRequestFullscreen?: unknown;
        }
      ).webkitRequestFullscreen
    ) &&
    !/iPhone|iPad|iPod/i.test(navigator.userAgent)
  );
}

/** Larger tap targets; no iOS fullscreen dead-end. */
export function SiteChromeMobile() {
  const phase = useGameStore((s) => s.phase);
  const lockerBeat = useGameStore((s) => s.lockerBeat);
  const muted = useGameStore((s) => s.muted);
  const setMuted = useGameStore((s) => s.setMuted);
  const skipToReveal = useGameStore((s) => s.skipToReveal);
  const setTunnelReady = useGameStore((s) => s.setTunnelReady);
  const finishLoading = useGameStore((s) => s.finishLoading);
  const showFs = canFullscreen();
  const fs = useFullscreenFlag();

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setCrowdIntensity(next ? 0 : 0.45);
    setIntroMuted(next);
  };

  const onSkip = () => {
    if (phase === 'loading') {
      finishLoading();
      return;
    }
    if (phase === 'landing') {
      // Skip on landing → portfolio site (path buttons already offer full game)
      void unlockAudio()
        .then(() => preloadAudio())
        .then(() => skipToReveal());
      return;
    }
    if (phase === 'locker') {
      const s = useGameStore.getState();
      if (s.lockerBeat === 'cinematic') {
        s.showLockerIdentity();
        s.setOverlayOpacity(0);
        return;
      }
      if (s.lockerBeat === 'claim') {
        s.finishLockerClaim();
        return;
      }
      if (s.lockerBeat === 'identity') {
        s.confirmLockerIdentity();
        return;
      }
      stopIntroBed(2.4);
      window.setTimeout(() => {
        void play('door', { gain: 1.1, rate: 0.96 });
      }, 700);
      window.setTimeout(() => {
        void startTunnelBed();
        s.enterTunnelFromLocker();
        s.setOverlayOpacity(1);
      }, 1400);
      return;
    }
    if (phase === 'tunnel') {
      setTunnelReady(true);
      return;
    }
    if (phase === 'reveal') return;
    skipToReveal();
  };

  const fsLabel = fs ? 'Exit FS' : 'Full screen';

  // Identity / claim owns the bottom of the screen — no skip / mute chrome
  if (phase === 'locker' && (lockerBeat === 'identity' || lockerBeat === 'claim')) {
    return null;
  }

  if (phase === 'reveal') {
    return (
      <nav className="site-m" aria-label="Experience controls">
        <button
          type="button"
          className="site-m__btn site-m__btn--icon"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          <SpeakerIcon muted={muted} />
        </button>
        {showFs && (
          <button
            type="button"
            className="site-m__btn"
            onPointerDown={onFullscreenButtonPointerDown}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-pressed={fs}
          >
            {fsLabel}
          </button>
        )}
      </nav>
    );
  }

  return (
    <nav className="site-m" aria-label="Experience controls">
      <button type="button" className="site-m__btn" onClick={onSkip}>
        Skip
      </button>
      <button
        type="button"
        className="site-m__btn site-m__btn--icon"
        onClick={toggleMute}
        aria-label={muted ? 'Unmute' : 'Mute'}
      >
        <SpeakerIcon muted={muted} />
      </button>
      {showFs && (
        <button
          type="button"
          className="site-m__btn"
          onPointerDown={onFullscreenButtonPointerDown}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          aria-pressed={fs}
        >
          {fsLabel}
        </button>
      )}
    </nav>
  );
}
